/**
 * EmissionChecklist.
 *
 * Componente que vive en /dashboard. Pinta el "camino completo" desde
 * onboarding hasta primera factura en producción real:
 *
 *  1. ✅ Profile fiscal completo
 *  2. ✅ Certificado configurado (mandato Taxor o BYO)
 *  3. ✅ Resolución DIAN cargada
 *  4. ⏳ Alerta: resolución consumida o por vencer (link a gestionar)
 *  5. ⏳ Primer cliente catalogado (link a /clientes)
 *  6. ⏳ Primer producto catalogado (link a /productos)
 *  7. ⏳ Set de pruebas DIAN (10 FE + 1 NC + 1 ND aceptados)
 *  8. ⏳ Pase a producción solicitado a DIAN
 *
 * Cada paso muestra:
 *   - Icono según estado: ✅ done, ⚠️ warning, ⏳ pending, ❌ blocker
 *   - Texto explicativo (1 línea)
 *   - Link CTA si pendiente
 *
 * Se hidrata con 5 queries en paralelo. Auto-refetch cada 60s para
 * reflejar avance de set de pruebas en vivo.
 */

import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  CheckCircle2,
  ChevronRight,
  Circle,
  FileWarning,
  Loader2,
  ShieldAlert,
} from 'lucide-react'

import {
  certApi,
  customerApi,
  habilitacionApi,
  productApi,
  profileApi,
} from '@/lib/api'
import { ROUTES } from '@/lib/constants'

type StepStatus = 'done' | 'pending' | 'warning' | 'blocker'

interface Step {
  id: string
  title: string
  status: StepStatus
  detail: string
  cta?: { label: string; to: string }
}

export function EmissionChecklist({ activeOrgId }: { activeOrgId: string }) {
  // Lanzamos todas las queries en paralelo. React Query las gestiona.
  const profile = useQuery({
    queryKey: ['settings-profile', activeOrgId],
    queryFn: () => profileApi.get(activeOrgId),
    enabled: !!activeOrgId,
  })
  const resStatus = useQuery({
    queryKey: ['resolution-status', activeOrgId],
    queryFn: () => profileApi.resolutionStatus(activeOrgId),
    enabled: !!activeOrgId && !!profile.data,
    refetchInterval: 60_000,
  })
  const cert = useQuery({
    queryKey: ['settings-cert', activeOrgId],
    queryFn: () => certApi.get(activeOrgId),
    enabled: !!activeOrgId,
    retry: false,
  })
  const customers = useQuery({
    queryKey: ['customers-count', activeOrgId],
    queryFn: () => customerApi.list(activeOrgId, { limit: 1 }),
    enabled: !!activeOrgId,
  })
  const products = useQuery({
    queryKey: ['products-count', activeOrgId],
    queryFn: () => productApi.list(activeOrgId, { limit: 1 }),
    enabled: !!activeOrgId,
  })
  const habStatus = useQuery({
    queryKey: ['habilitacion-status', activeOrgId],
    queryFn: () => habilitacionApi.status(activeOrgId),
    enabled: !!activeOrgId,
    refetchInterval: 60_000,
  })

  const loading =
    profile.isLoading ||
    cert.isLoading ||
    customers.isLoading ||
    products.isLoading ||
    habStatus.isLoading

  // Construcción del checklist.
  const steps: Step[] = []

  // 1. Profile.
  steps.push({
    id: 'profile',
    title: 'Datos fiscales del emisor',
    status: profile.data?.onboarding_completed ? 'done' : 'pending',
    detail: profile.data?.legal_name
      ? `${profile.data.legal_name} · CIIU ${profile.data.ciiu}`
      : 'Completa razón social, NIT, dirección, régimen.',
    cta: profile.data?.onboarding_completed
      ? undefined
      : { label: 'Completar', to: ROUTES.SETTINGS_PROFILE },
  })

  // 2. Cert.
  const hasMandate = !cert.data?.has_byo_cert
  const byoExpiringSoon =
    cert.data?.has_byo_cert &&
    cert.data.cert &&
    cert.data.cert.days_until_expiration < 30
  steps.push({
    id: 'cert',
    title: 'Certificado de firma',
    status: byoExpiringSoon ? 'warning' : 'done',
    detail: hasMandate
      ? 'Mandato Taxor: firmamos con nuestro cert ECD bajo Contrato de Mandato.'
      : `Cert propio (BYO) · vence en ${cert.data?.cert?.days_until_expiration ?? '?'} días`,
    cta: byoExpiringSoon ? { label: 'Renovar', to: ROUTES.SETTINGS } : undefined,
  })

  // 3. Resolución cargada.
  const hasResolution = !!profile.data?.resolution_prefix
  steps.push({
    id: 'resolution-loaded',
    title: 'Resolución DIAN cargada',
    status: hasResolution ? 'done' : 'pending',
    detail: hasResolution
      ? `${profile.data?.resolution_prefix} · rango ${profile.data?.resolution_from?.toLocaleString('es-CO')}-${profile.data?.resolution_to?.toLocaleString('es-CO')}`
      : 'Sin resolución activa.',
    cta: hasResolution
      ? undefined
      : { label: 'Cargar', to: ROUTES.SETTINGS_RESOLUTION },
  })

  // 4. Salud de la resolución.
  if (resStatus.data && hasResolution) {
    const lvl = resStatus.data.alert_level
    let status: StepStatus = 'done'
    let detail = `${resStatus.data.percent_consumed.toFixed(0)}% consumida · vence en ${resStatus.data.days_until_expiry} días`
    if (lvl === 'warning') status = 'warning'
    if (lvl === 'critical') status = 'warning'
    if (lvl === 'exhausted') {
      status = 'blocker'
      detail = 'Rango DIAN agotado — emisión bloqueada.'
    }
    steps.push({
      id: 'resolution-health',
      title: 'Resolución vigente',
      status,
      detail:
        lvl === 'ok' ? `${detail} · todo bien` : `${detail} · ${resStatus.data.alert_reasons[0] ?? ''}`,
      cta:
        lvl === 'ok'
          ? undefined
          : { label: 'Gestionar', to: ROUTES.SETTINGS_RESOLUTION },
    })
  }

  // 5. Primer cliente.
  const customersCount = customers.data?.items?.length ?? 0
  const customersTotal =
    customers.data?.total ?? (customersCount > 0 ? customersCount : 0)
  steps.push({
    id: 'first-customer',
    title: 'Catálogo de clientes',
    status: customersTotal > 0 ? 'done' : 'pending',
    detail:
      customersTotal > 0
        ? `${customersTotal} cliente${customersTotal === 1 ? '' : 's'} registrados`
        : 'Crea tu primer adquirente para empezar a facturar.',
    cta:
      customersTotal > 0
        ? undefined
        : { label: 'Crear cliente', to: ROUTES.CUSTOMER_NEW },
  })

  // 6. Primer producto.
  const productsCount = products.data?.items?.length ?? 0
  const productsTotal =
    products.data?.total ?? (productsCount > 0 ? productsCount : 0)
  steps.push({
    id: 'first-product',
    title: 'Catálogo de productos',
    status: productsTotal > 0 ? 'done' : 'pending',
    detail:
      productsTotal > 0
        ? `${productsTotal} producto${productsTotal === 1 ? '' : 's'} registrados`
        : 'Crea al menos un producto/servicio facturable.',
    cta:
      productsTotal > 0
        ? undefined
        : { label: 'Crear producto', to: ROUTES.PRODUCT_NEW },
  })

  // 7. Set de pruebas DIAN.
  if (habStatus.data) {
    const hs = habStatus.data
    const allDone =
      hs.invoices_accepted >= 10 && hs.credit_note_accepted && hs.debit_note_accepted
    let detail = ''
    if (allDone) {
      detail = `10/10 FE · 1 NC · 1 ND · habilitación completa`
    } else {
      detail = `${hs.invoices_accepted}/10 FE · NC ${hs.credit_note_accepted ? '✓' : '✗'} · ND ${hs.debit_note_accepted ? '✓' : '✗'} · próximo: ${hs.next_action}`
    }
    steps.push({
      id: 'habilitacion',
      title: 'Set de pruebas DIAN',
      status: allDone ? 'done' : 'pending',
      detail,
      cta: allDone
        ? undefined
        : { label: 'Continuar set', to: ROUTES.HABILITACION },
    })
  }

  // 8. Pase a producción (no UI hoy, deuda).
  const habComplete =
    habStatus.data &&
    habStatus.data.invoices_accepted >= 10 &&
    habStatus.data.credit_note_accepted &&
    habStatus.data.debit_note_accepted
  steps.push({
    id: 'production-request',
    title: 'Pase a producción',
    status: habComplete ? 'pending' : 'pending',
    detail: habComplete
      ? 'Set listo. Solicita oficio DIAN de pase a producción.'
      : 'Disponible cuando el set de pruebas esté completo.',
  })

  if (loading) {
    return (
      <section className="mt-6 rounded-xl border border-border bg-bg-surface p-6">
        <div className="flex items-center gap-2 text-sm text-fg-muted">
          <Loader2 className="h-4 w-4 animate-spin" /> Cargando checklist…
        </div>
      </section>
    )
  }

  const doneCount = steps.filter((s) => s.status === 'done').length
  const totalCount = steps.length
  const allDone = doneCount === totalCount

  return (
    <section className="mt-6 rounded-xl border border-border bg-bg-surface p-6">
      <header className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Camino a tu primera factura</h2>
          <p className="mt-1 text-sm text-fg-secondary">
            {allDone
              ? 'Todo listo. Puedes emitir en producción.'
              : `${doneCount} de ${totalCount} pasos completados.`}
          </p>
        </div>
        <span className="font-mono text-sm text-fg-secondary">
          {doneCount}/{totalCount}
        </span>
      </header>
      <div className="mt-2 h-1.5 w-full rounded-full bg-bg-app overflow-hidden">
        <div
          className="h-full bg-gradient-to-r from-brand-500 to-rose-500 transition-all"
          style={{ width: `${(doneCount / totalCount) * 100}%` }}
        />
      </div>
      <ul className="mt-5 divide-y divide-border">
        {steps.map((step) => (
          <StepRow key={step.id} step={step} />
        ))}
      </ul>
    </section>
  )
}

function StepRow({ step }: { step: Step }) {
  const Icon =
    step.status === 'done'
      ? CheckCircle2
      : step.status === 'warning'
        ? FileWarning
        : step.status === 'blocker'
          ? ShieldAlert
          : Circle
  const iconColor =
    step.status === 'done'
      ? 'text-success-fg'
      : step.status === 'warning'
        ? 'text-yellow-600 dark:text-yellow-400'
        : step.status === 'blocker'
          ? 'text-error-fg'
          : 'text-fg-muted'

  return (
    <li className="flex items-center gap-3 py-3">
      <Icon className={`h-5 w-5 flex-none ${iconColor}`} />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-fg-primary">{step.title}</p>
        <p className="mt-0.5 text-xs text-fg-muted truncate">{step.detail}</p>
      </div>
      {step.cta && (
        <Link
          to={step.cta.to}
          className="inline-flex items-center gap-1 rounded-md border border-border bg-bg-app px-3 py-1.5 text-xs font-medium text-fg-primary hover:bg-bg-elevated"
        >
          {step.cta.label} <ChevronRight className="h-3 w-3" />
        </Link>
      )}
    </li>
  )
}
